import { ArrowDown, ArrowUp } from 'lucide-react'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { callAppsScript } from '@/lib/agents/sheets-tools'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { type Platform, usePlatform } from '@/lib/platform'
import { getAuthToken, useSupabaseAuth } from '@/lib/supabase'

const ONBOARDING_SEEN_KEY = 'onboarding_seen'
const ONBOARDING_CACHE_KEY = 'pipedreamer_onboarding_seen'

/**
 * Check if user has seen onboarding (from Supabase user settings)
 * Returns false if user is not authenticated or API call fails.
 * Uses localStorage as a fast cache to avoid a backend round-trip for returning users.
 */
export async function hasSeenOnboarding(): Promise<boolean> {
  // Fast path: check localStorage cache first
  try {
    if (localStorage.getItem(ONBOARDING_CACHE_KEY) === 'true') {
      return true
    }
  } catch {
    // localStorage may be unavailable in some environments
  }

  const token = await getAuthToken()
  if (!token) {
    // Not authenticated, show onboarding
    return false
  }

  try {
    const response = await fetch(
      `${BACKEND_URL}/api/settings/${ONBOARDING_SEEN_KEY}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Check onboarding status')
      }
      // API call failed, show onboarding
      return false
    }

    const data = await response.json()
    const seen = data.value === true

    // Cache the result in localStorage for next load
    if (seen) {
      try {
        localStorage.setItem(ONBOARDING_CACHE_KEY, 'true')
      } catch {
        // Ignore storage errors
      }
    }

    return seen
  } catch (error) {
    console.error('Failed to check onboarding status:', error)
    // On error, show onboarding
    return false
  }
}

/**
 * Mark onboarding as seen (store in Supabase user settings)
 */
async function markOnboardingSeen(): Promise<void> {
  const token = await getAuthToken()
  if (!token) {
    console.warn('Cannot mark onboarding as seen: user not authenticated')
    return
  }

  try {
    const response = await fetch(
      `${BACKEND_URL}/api/settings/${ONBOARDING_SEEN_KEY}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ value: true }),
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        await handleUnauthorized(response, 'Mark onboarding as seen')
        return
      }
      console.error('Failed to mark onboarding as seen: API returned error')
      return
    }

    // Cache in localStorage so future loads skip the API call
    try {
      localStorage.setItem(ONBOARDING_CACHE_KEY, 'true')
    } catch {
      // Ignore storage errors
    }
  } catch (error) {
    console.error('Failed to mark onboarding as seen:', error)
  }
}

interface OnboardingCarouselProps {
  onComplete: () => void
}

const DEMO_PROMPT =
  'Clean up this bank statement. Add a column with readable descriptions, format the dates in column A to MM/DD/YY, and categorize each expense (e.g., Food, Transport, Utilities). Highlight the row yellow if over $75, green if under. Make it into a table that is sortable and filterable. Build a summary table with category totals. Make a pie chart'

const bankTransactionData: (string | number)[][] = [
  ['Date', 'Title', 'Cost'],
  ['1/1/24', 'STARBUCKS STORE #4928 SEATTLE WA', '$5.45'],
  ['1/2/24', 'UBER * TRIP 1234 HELP.UBER.COM', '$15.20'],
  ['01.03.24', 'LANDLORD PROPERTIES INC - RENT', '$1,500.00'],
  ['Jan 4 2024', 'SAFEWAY #1234 ANYTOWN', '$84.12'],
  ['1/5/24', 'SHELL OIL 987654321', '$45.00'],
  ['1/6/24', 'NETFLIX.COM DIGITAL SUBSCRIPTION', '$19.99'],
  ['1/7/24', 'AMAZON.COM*MB1234567 AMZN', '$124.50'],
  ['1/8/24', 'CHIPOTLE 0987 ONLINE', '$14.30'],
  ['1/9/24', 'CITY WATER & POWER BILL', '$85.00'],
  ['10-Jan-24', 'GYM MEMBERSHIP*ULTRAFIT', '$50.00'],
  ['11-Jan-24', 'UBER * TRIP 5678 HELP.UBER.COM', '$22.10'],
  ['1/12/24', 'STARBUCKS STORE #1111', '$4.75'],
  ['1/13/24', 'WHOLEFOODS MARKET #55', '$112.40'],
  ['1/14/24', 'VERIZON WIRELESS*PAYMENT', '$90.00'],
  ['15-Jan-24', 'DELTA AIRLINES*RESERVATION', '$350.00'],
  ['01.16.24', 'WALMART SUPERCENTER #88', '$65.20'],
  ['01/17/24, 9:30 AM', 'SPOTIFY USA', '$10.99'],
  ['1/18/24', 'ADOBE *CREATIVE CLOUD', '$52.99'],
  ['1/19/24', 'EXXON MOBIL 12345', '$38.00'],
  ['1/20/24', 'TARG-ET #223344', '$42.15'],
  ['Jan 21 24', 'APPLE.COM/BILL', '$0.99'],
  ['1/22/24', 'MCDONALDS F1234', '$8.50'],
  ['23-Jan-24', 'MARRIOT HOTELS NY', '$210.00'],
  ['1/24/24', 'STARBUCKS STORE #9999', '$6.20'],
  ['1/25/24', 'COMCAST CABLE BILL', '$75.00'],
  ['1/5/24', 'AMZN MKTP US*MB12345', '$45.99'],
  ['Jan 6 2024', 'SHELL OIL 123456789', '$62.50'],
  ['1/7/24', 'COMPCAST CABLE INTERNET', '$89.99'],
  ['08.01.2024', 'WHOLEFDS SFO 10234', '$112.10'],
  ['10-Jan-24', 'UBER * TRIP RIDE', '$18.50'],
  ['1/12/24', 'LANDLORD PROP MGMT RENT', '$1,500.00'],
  ['13-Jan-24', 'AT&T*MOBILITY 800-331-0500', '$75.00'],
  ['01.15.2024', 'NETFLIX.COM', '$15.99'],
  ['16-Jan-24', 'CITY WATER & POWER', '$125.40'],
  ['1/18/24', 'CHIPOTLE 1234', '$14.25'],
  ['1/19/24', 'GYM*MEMBERSHIP MONTHLY', '$50.00'],
  ['01.20.2024', 'DELTA AIRLINES 0061234567890', '$350.00'],
  ['Jan 21 2024', 'WAG*WALKER DOG WALK', '$25.00'],
  ['2024.01.22', 'APPLE.COM/BILL', '$0.99'],
  ['23/01/24', 'SAFEWAY STORE 4567', '$55.30'],
  ['25-Jan-24', 'GEICO INSURANCE PREMIUM', '$210.00'],
  ['1/26/24', 'STARBUCKS STORE #1234', '$5.75'],
  ['1/27/24', '7-ELEVEN 123456', '$12.40'],
  ['28.01.2024', 'BLUE BOTTLE COFFEE', '$6.50'],
  ['Jan 29 2024', 'CHASE CREDIT CARD PMT', '$500.00'],
]

async function createDemoSheetExcel(): Promise<void> {
  const Excel = window.Excel
  if (!Excel) {
    throw new Error('Excel runtime not available')
  }

  await Excel.run(async (context: Excel.RequestContext) => {
    const sheetName = 'Bank Transactions'
    const worksheets = context.workbook.worksheets

    // Check if sheet already exists and delete it
    // Load worksheets to check for existing sheet
    worksheets.load('items')
    await context.sync()

    // Load name property for all sheets
    for (const sheet of worksheets.items) {
      sheet.load('name')
    }
    await context.sync()

    const existingSheet = worksheets.items.find((s) => s.name === sheetName)
    if (existingSheet) {
      existingSheet.delete()
      await context.sync()
    }

    // Create new sheet
    const sheet = worksheets.add(sheetName)

    // Add data starting from row 1 (no prompt text, just raw data)
    const dataRange = sheet.getRange('A1')
    const finalDataRange = dataRange.getResizedRange(
      bankTransactionData.length - 1,
      bankTransactionData[0]?.length ? bankTransactionData[0].length - 1 : 0
    )
    finalDataRange.values = bankTransactionData
    finalDataRange.format.autofitColumns()

    sheet.activate()
    await context.sync()
  })
}

async function createDemoSheetSheets(): Promise<void> {
  // Create the worksheet
  await callAppsScript<{
    success: boolean
    sheetName: string
    created: boolean
  }>('createWorksheet', 'Bank Transactions')

  // Write the data
  await callAppsScript<{
    success: boolean
    address: string
    rowsWritten: number
    columnsWritten: number
  }>('writeRange', 'A1', bankTransactionData, true)
}

async function createDemoSheet(platform: Platform): Promise<void> {
  if (platform === 'sheets') {
    await createDemoSheetSheets()
  } else {
    await createDemoSheetExcel()
  }
}

function OnboardingCarousel({ onComplete }: OnboardingCarouselProps) {
  const platform = usePlatform()
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [creating, setCreating] = useState(false)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const screens = [
    {
      title: 'Say what you want',
      content: (
        <div className="rounded-lg border overflow-hidden bg-background">
          <img
            src="/assets/onboarding/step-1-chat.png"
            alt="Step 1: Chat interface"
            className="w-full h-auto object-contain"
          />
        </div>
      ),
    },
    {
      title: 'Let AI do the work',
      content: (
        <div className="rounded-lg border overflow-hidden bg-background">
          <img
            src="/assets/onboarding/step-2-tools.png"
            alt="Step 2: Tools interface"
            className="w-[50%] h-auto object-contain"
          />
        </div>
      ),
    },
    {
      title: 'Watch the magic happen',
      content: (
        <div className="rounded-lg border overflow-hidden bg-background">
          <img
            src="/assets/onboarding/step-3-magic.png"
            alt="Step 3: Magic results"
            className="w-full h-auto object-contain"
          />
        </div>
      ),
    },
  ]

  useEffect(() => {
    if (!api) return

    const updateState = () => {
      setCurrent(api.selectedScrollSnap())
      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }

    updateState()

    api.on('select', updateState)
    api.on('reInit', updateState)

    return () => {
      api.off('select', updateState)
      api.off('reInit', updateState)
    }
  }, [api])

  const handleNext = () => {
    api?.scrollNext()
  }

  const handlePrev = () => {
    api?.scrollPrev()
  }

  const handleSkip = async () => {
    await markOnboardingSeen()
    onComplete()
  }

  const handleCreateDemo = async () => {
    setCreating(true)
    try {
      await createDemoSheet(platform)
      // Store the pre-fill prompt text in sessionStorage
      sessionStorage.setItem('onboardingPrefillPrompt', DEMO_PROMPT)
      await markOnboardingSeen()
      onComplete()
    } catch (error) {
      console.error('Failed to create demo sheet:', error)
      alert('Failed to create demo sheet. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex flex-col h-full py-6 px-2 items-center justify-center">
      {/* Header with logo and welcome */}
      <div className="text-center mb-4 shrink-0">
        <p className="text-slate-500 text-sm mb-1">Welcome to</p>
        <div className="flex items-center justify-center gap-2">
          <img
            src="/assets/logo-64.png"
            alt="Pipedreamer"
            className="w-6 h-6 object-contain"
          />
          <h1 className="text-2xl font-semibold tracking-tight">Pipedreamer</h1>
        </div>
      </div>
      {/* Carousel content */}
      <Carousel
        setApi={setApi}
        orientation="vertical"
        className="w-full flex-1 min-h-0 flex flex-col"
      >
        <CarouselContent className="flex-1 min-h-0 h-full">
          {screens.map((screen, index) => (
            <CarouselItem
              key={`${screen.title}-${index}`}
              className="flex flex-col space-y-4 h-full overflow-y-auto overflow-x-hidden items-center justify-center px-2"
            >
              {/* Back button right above content */}
              {index === current && canScrollPrev && (
                <div className="shrink-0">
                  <Button variant="ghost" size="sm" onClick={handlePrev}>
                    <ArrowUp className="mr-1" />
                    Back
                  </Button>
                </div>
              )}

              <h2 className="text-xl font-semibold text-center shrink-0">
                {screen.title}
              </h2>
              <div className="text-foreground w-full shrink-0">
                {screen.content}
              </div>

              {/* Next button right below content */}
              {index === current && canScrollNext && (
                <div className="shrink-0">
                  <Button variant="ghost" size="sm" onClick={handleNext}>
                    Next
                    <ArrowDown className="ml-1" />
                  </Button>
                </div>
              )}

              {/* Action buttons for last screen */}
              {index === screens.length - 1 && (
                <div className="flex flex-col items-center gap-4 shrink-0 mt-4">
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={handleCreateDemo}
                      disabled={creating}
                      size="sm"
                    >
                      {creating ? 'Creating...' : 'Create Demo Sheet'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleSkip}>
                      Skip Demo
                    </Button>
                  </div>
                </div>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  )
}

interface OnboardingGateProps {
  children: React.ReactNode
}

export default function OnboardingGate({ children }: OnboardingGateProps) {
  const { isAuthenticated, loading } = useSupabaseAuth()
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!isAuthenticated || loading) {
        setIsChecking(false)
        return
      }

      try {
        const seen = await hasSeenOnboarding()
        if (!seen) {
          setShowOnboarding(true)
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error)
        // On error, don't show onboarding to avoid blocking user
      } finally {
        setIsChecking(false)
      }
    }

    checkOnboarding()
  }, [isAuthenticated, loading])

  // Show loading state while checking (or show children if not authenticated)
  if (isChecking || loading) {
    return <>{children}</>
  }

  if (showOnboarding) {
    return <OnboardingCarousel onComplete={() => setShowOnboarding(false)} />
  }

  return <>{children}</>
}
